import { useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import {
  useLeaderboard, useGroupedLeaderboard,
  useInviteFriend, useFriends,
  useAcceptFriend, useRemoveFriend,
} from '../lib/api';
import type { LeaderboardEntry } from '@shared/types';
import { VerifiedBadge } from '../components/VerifiedBadge';

const SCOPES = [
  { code: 'friends', label: 'Friends' },
  { code: 'class', label: 'Class' },
  { code: 'school', label: 'School' },
  { code: 'exam', label: 'Exam' },
  { code: 'global', label: 'Global' },
] as const;

export function LeaderboardPage() {
  const { scope } = useParams();
  const activeScope = scope ?? 'friends';

  return (
    <div className="max-w-2xl">
      <div className="flex gap-2 mb-4 text-sm overflow-x-auto no-scrollbar">
        {SCOPES.map((s) => (
          <NavLink key={s.code} to={`/leaderboard/${s.code}`}
                   className={({ isActive }) => `shrink-0 px-3 py-1 rounded-full border ${isActive ? 'bg-squishy-primary text-white' : ''}`}>
            {s.label}
          </NavLink>
        ))}
      </div>

      {activeScope === 'friends' && <FriendsTab />}
      {activeScope === 'class' && <GroupedTab scope="class" />}
      {activeScope === 'school' && <GroupedTab scope="school" />}
      {activeScope === 'exam' && <GroupedTab scope="exam" />}
      {activeScope === 'global' && <GlobalTab />}
    </div>
  );
}

// ── Friends Tab ──────────────────────────────────────────────────────────────
function FriendsTab() {
  const { data } = useLeaderboard('friends');
  const invite = useInviteFriend();
  const friends = useFriends();
  const accept = useAcceptFriend();
  const remove = useRemoveFriend();
  const [q, setQ] = useState('');
  const [removeTarget, setRemoveTarget] = useState<LeaderboardEntry | null>(null);

  const incoming = (friends.data?.friends ?? []).filter(
    (f) => f.status === 'pending' && f.direction === 'incoming',
  );
  const outgoing = (friends.data?.friends ?? []).filter(
    (f) => f.status === 'pending' && f.direction === 'outgoing',
  );

  const handleInvite = async () => {
    const val = q.trim();
    if (!val) return;
    invite.reset();
    if (val.includes('@')) await invite.mutateAsync({ email: val });
    else await invite.mutateAsync({ handle: val });
    setQ('');
  };

  return (
    <div className="space-y-4">
      {/* Add friend */}
      <div className="bg-white border rounded-xl p-4">
        <form onSubmit={(e) => { e.preventDefault(); handleInvite(); }} className="flex gap-2">
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); invite.reset(); }}
            placeholder="Name, email, or username"
            className="flex-1 border rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={invite.isPending || q.trim().length < 2}
            className="bg-squishy-primary text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          >Send</button>
        </form>
        {invite.isSuccess && <p className="text-xs text-emerald-600 mt-1">If that user exists, they'll receive your friend invite!</p>}
        {invite.isError && <p className="text-xs text-red-500 mt-1">Something went wrong. Please try again.</p>}
      </div>

      {/* Incoming requests */}
      {incoming.length > 0 && (
        <div className="bg-white border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-squishy-navy mb-2">Friend requests ({incoming.length})</h3>
          <ul className="divide-y">
            {incoming.map((f) => (
              <li key={f.id} className="flex items-center gap-3 py-2 text-sm">
                <UserAvatar name={formatFriendName(f)} />
                <div className="flex-1 font-medium">{formatFriendName(f)}</div>
                <button onClick={() => accept.mutate(f.id)}
                        className="bg-squishy-primary text-white px-3 py-1 text-xs rounded-full">Accept</button>
                <button onClick={() => remove.mutate(f.id)}
                        className="border px-3 py-1 text-xs rounded-full text-slate-500">Decline</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Outgoing (sent) requests — pending until accepted */}
      {outgoing.length > 0 && (
        <div className="bg-white border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-squishy-navy mb-2">Sent requests ({outgoing.length})</h3>
          <ul className="divide-y">
            {outgoing.map((f) => (
              <li key={f.id} className="flex items-center gap-3 py-2 text-sm">
                <UserAvatar name={formatFriendName(f)} />
                <div className="flex-1 font-medium min-w-0 truncate">{formatFriendName(f)}</div>
                <span className="text-xs text-slate-400 shrink-0">Pending</span>
                <button
                  type="button"
                  onClick={() => remove.mutate(f.id)}
                  disabled={remove.isPending}
                  aria-label="Cancel request"
                  className="shrink-0 p-2 -mr-1 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 active:scale-90 transition disabled:opacity-50"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                    <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.58.177-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Leaderboard */}
      <h2 className="text-lg font-semibold text-squishy-navy">Friends</h2>
      <LeaderboardList entries={data?.entries ?? []} onRemove={setRemoveTarget} />
      {data?.currentUserRank && (
        <p className="text-xs text-slate-500 mt-2">Your rank: #{data.currentUserRank} of {data.totalUsers}</p>
      )}

      {/* Remove-friend confirmation */}
      {removeTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => { if (!remove.isPending) setRemoveTarget(null); }}
        >
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-squishy-dark">Remove friend?</h3>
            <p className="text-sm text-slate-600 mt-2">
              Remove <span className="font-semibold">{removeTarget.displayName}</span> from your friends? You'll both stop appearing in each other's friends list and rankings. You can add them again later.
            </p>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setRemoveTarget(null)}
                disabled={remove.isPending}
                className="flex-1 border rounded-xl py-2.5 text-sm font-medium text-slate-600 disabled:opacity-50"
              >Cancel</button>
              <button
                onClick={async () => {
                  try { await remove.mutateAsync(removeTarget.userId); } finally { setRemoveTarget(null); }
                }}
                disabled={remove.isPending}
                className="flex-1 bg-red-500 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
              >{remove.isPending ? 'Removing…' : 'Remove'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Grouped Tab (Class / School / Exam) ──────────────────────────────────────
function GroupedTab({ scope }: { scope: 'class' | 'school' | 'exam' }) {
  const { data } = useGroupedLeaderboard(scope);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  const groups = data?.groups ?? [];
  const labels = { class: 'Classes', school: 'Schools', exam: 'Exams' } as const;
  const singleLabel = { class: 'class_section', school: 'school', exam: 'exam' } as const;

  if (groups.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500 text-sm">
        {scope === 'class' ? "You aren't enrolled in any classes yet."
          : scope === 'school' ? "No school found for your account."
          : "No exams found. Start studying an exam to see rankings."}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-squishy-navy">My {labels[scope]}</h2>
      {groups.map((g) => (
        <GroupSection
          key={g.groupId}
          group={g}
          scopeType={singleLabel[scope]}
          onExpand={() => setExpandedGroup(g.groupId)}
        />
      ))}

      {/* Expanded overlay */}
      {expandedGroup && (
        <ExpandedLeaderboard
          groupId={expandedGroup}
          scopeType={singleLabel[scope]}
          groups={groups}
          onClose={() => setExpandedGroup(null)}
        />
      )}
    </div>
  );
}

function GroupSection({ group, scopeType, onExpand }: {
  group: { groupId: string; groupName: string; entries: LeaderboardEntry[] };
  scopeType: string;
  onExpand: () => void;
}) {
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-semibold text-squishy-navy">{group.groupName}</h3>
        <button onClick={onExpand} className="text-xs text-squishy-tealDark hover:underline">
          More &gt;&gt;
        </button>
      </div>
      {group.entries.length === 0 ? (
        <p className="text-xs text-slate-400 py-2">No activity in the last 30 days</p>
      ) : (
        <ol className="divide-y">
          {group.entries.slice(0, 3).map((e) => (
            <LeaderboardRow key={e.userId} entry={e} />
          ))}
        </ol>
      )}
    </div>
  );
}

// ── Global Tab ───────────────────────────────────────────────────────────────
function GlobalTab() {
  const { data } = useLeaderboard('global');
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-squishy-navy">Global</h2>
      <LeaderboardList entries={data?.entries ?? []} />
      {data?.currentUserRank && (
        <p className="text-xs text-slate-500 mt-2">Your rank: #{data.currentUserRank} of {data.totalUsers}</p>
      )}
    </div>
  );
}

// ── Expanded Leaderboard Overlay ─────────────────────────────────────────────
function ExpandedLeaderboard({ groupId, scopeType, groups, onClose }: {
  groupId: string;
  scopeType: string;
  groups: { groupId: string; groupName: string; entries: LeaderboardEntry[] }[];
  onClose: () => void;
}) {
  const group = groups.find((g) => g.groupId === groupId);
  // Fetch full leaderboard for this scope
  const scopeMap: Record<string, string> = {
    'class_section': 'class_section',
    'school': 'school',
    'exam': 'exam',
  };
  const { data } = useLeaderboard(scopeMap[scopeType] as any, groupId);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div
        className="absolute inset-x-0 bottom-0 top-12 bg-white rounded-t-2xl p-5 overflow-y-auto animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-squishy-navy">{group?.groupName ?? data?.scopeName ?? 'Rankings'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">✕</button>
        </div>
        <p className="text-xs text-slate-500 mb-3">Last 30 days</p>
        <LeaderboardList entries={data?.entries ?? group?.entries ?? []} />
        {data?.currentUserRank && (
          <p className="text-xs text-slate-500 mt-3">Your rank: #{data.currentUserRank} of {data.totalUsers}</p>
        )}
      </div>
    </div>
  );
}

// ── Shared Components ────────────────────────────────────────────────────────

function LeaderboardList({ entries, onRemove }: { entries: LeaderboardEntry[]; onRemove?: (e: LeaderboardEntry) => void }) {
  if (entries.length === 0) {
    return <p className="text-sm text-slate-400 py-4 text-center">No activity in the last 30 days</p>;
  }
  return (
    <ol className="bg-white rounded-xl border divide-y overflow-hidden">
      {entries.map((e) => (
        <LeaderboardRow key={e.userId} entry={e} onRemove={onRemove} />
      ))}
    </ol>
  );
}

function LeaderboardRow({ entry: e, onRemove }: { entry: LeaderboardEntry; onRemove?: (e: LeaderboardEntry) => void }) {
  const invite = useInviteFriend();
  const [showAdd, setShowAdd] = useState(false);
  // In the Friends tab (onRemove set) the row is already a friend: show a delete
  // button and suppress the add-friend popover.
  const removable = Boolean(onRemove) && !e.isCurrentUser;

  return (
    <li className={`flex items-center gap-3 p-3 ${e.isCurrentUser ? 'bg-blue-50' : ''}`}>
      <div className="w-8 text-right font-mono text-sm text-slate-500">{e.rank}</div>
      <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center text-xs">
        {e.avatarUrl ? <img src={e.avatarUrl} alt="" className="w-full h-full object-cover" /> : e.displayName[0]}
      </div>
      <button
        className="flex-1 text-left font-medium text-sm hover:text-squishy-tealDark transition inline-flex items-center gap-1 min-w-0"
        onClick={() => { if (!e.isCurrentUser && !onRemove) setShowAdd(!showAdd); }}
      >
        <span className="truncate">{e.displayName}</span>
        {e.verifiedStudent && <VerifiedBadge />}
      </button>
      <div className="text-sm">L{e.level}</div>
      <div className="text-sm font-mono text-squishy-dark w-16 text-right">{e.xp.toLocaleString()} XP</div>

      {/* Inline add-friend popover (not shown for existing friends) */}
      {showAdd && !e.isCurrentUser && !onRemove && (
        <button
          onClick={async (ev) => {
            ev.stopPropagation();
            await invite.mutateAsync({ userId: e.userId });
            setShowAdd(false);
          }}
          disabled={invite.isPending}
          className="shrink-0 bg-squishy-primary text-white text-xs px-3 py-1 rounded-full disabled:opacity-50"
        >
          {invite.isSuccess ? 'Sent!' : invite.isPending ? '...' : '+ Friend'}
        </button>
      )}

      {/* Remove-friend button (Friends tab only) */}
      {removable && (
        <button
          type="button"
          onClick={() => onRemove!(e)}
          aria-label={`Remove ${e.displayName}`}
          className="shrink-0 -mr-1 p-2 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 active:scale-90 transition"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.58.177-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
          </svg>
        </button>
      )}
    </li>
  );
}

function UserAvatar({ name }: { name: string }) {
  return (
    <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-700">
      {name[0]?.replace('@', '')}
    </div>
  );
}

function formatFriendName(f: any): string {
  const real = `${f.first_name} ${f.last_name?.[0] ?? ''}.`;
  // Accepted friends always see your real name, even if you're anonymous / use a handle.
  if (f.status === 'accepted') return real;
  if (f.display_name_mode === 'custom' && f.custom_handle) return `@${f.custom_handle}`;
  if (f.display_name_mode === 'anonymous') return `Student #${f.id.slice(0, 4).toUpperCase()}`;
  return real;
}
